import ImageWithFallback from '@components/image/ImageWithFallback'
import AddressDisplay from '@components/shared/AddressDisplay'
import {
  SortableColumnHeader,
  Table,
  Td,
  Th,
  TrBody,
  TrHead,
} from '@components/shared/TableElements'
import { SearchResult } from 'hooks/useSearchResults'
import { useSortableData } from 'hooks/useSortableData'
import { TokenMetadataItem } from 'types/token'
import { abbreviateAddress, truncateString } from 'utils/formatting'
import { formatNumericValue, numberCompacter } from 'utils/numbers'
import { useTranslation } from 'next-i18next'
import { useVerifiedTokenList } from 'hooks/useVerfiedTokenList'
import { CheckBadgeIcon } from '@heroicons/react/20/solid'

const SearchTable = ({
  tokens,
  onClick,
  isDisabled,
}: {
  tokens: SearchResult[]
  onClick: (mint: string, token?: TokenMetadataItem) => void
  isDisabled?: (token: SearchResult) => boolean
}) => {
  const {
    items: sortedTableData,
    requestSort,
    sortConfig,
  } = useSortableData(tokens)
  const { t } = useTranslation('search')
  const { verifiedTokenList } = useVerifiedTokenList()

  return (
    <Table>
      <thead className="sticky top-0 z-10">
        <TrHead>
          <Th className="text-left">
            <SortableColumnHeader
              sortKey="symbol"
              sort={() => requestSort('symbol')}
              sortConfig={sortConfig}
              title={t('table.token')}
            />
          </Th>
          <Th>
            <div className="flex justify-end">
              <SortableColumnHeader
                sortKey="latest_price_usd"
                sort={() => requestSort('latest_price_usd')}
                sortConfig={sortConfig}
                title={t('table.price')}
              />
            </div>
          </Th>
          <Th>
            <div className="flex justify-end">
              <SortableColumnHeader
                sortKey="volume_24h_usd"
                sort={() => requestSort('volume_24h_usd')}
                sortConfig={sortConfig}
                title={t('table.volume-24h')}
              />
            </div>
          </Th>
        </TrHead>
      </thead>
      <tbody>
        {sortedTableData.map((token) => {
          const {
            latest_price_usd,
            mint,
            name,
            image_url,
            symbol,
            volume_24h_usd,
          } = token

          const disabled = isDisabled ? isDisabled(token) : false

          return (
            <TrBody
              key={`${mint}${name}${volume_24h_usd}`}
              className={`default-transition h-16 ${
                disabled
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer md:hover:bg-th-bkg-3'
              }`}
              onClick={() => !disabled && onClick(mint)}
            >
              <Td>
                <div className="flex items-center">
                  <ImageWithFallback
                    className="mr-2.5"
                    height={32}
                    width={32}
                    src={image_url}
                    alt={`${symbol} token logo`}
                  />
                  <div className="text-left">
                    <div className="mb-1 flex flex-wrap items-center">
                      <p className="mr-2 font-body text-sm font-bold leading-none text-th-fgd-1">
                        {symbol}{' '}
                      </p>
                      <div className="hidden sm:block">
                        {mint ? (
                          <AddressDisplay>
                            <p className="text-xs">{abbreviateAddress(mint)}</p>
                          </AddressDisplay>
                        ) : (
                          '–'
                        )}
                      </div>
                      <div>
                        {verifiedTokenList?.find((x) => x.id === mint) && (
                          <CheckBadgeIcon
                            className="text-th-active"
                            width={12}
                          />
                        )}
                      </div>
                    </div>
                    <p className="font-body text-xs leading-none">
                      {name ? truncateString(name, 20) : '–'}
                    </p>
                  </div>
                </div>
              </Td>
              <Td>
                <p className="text-right text-th-fgd-1">
                  ${formatNumericValue(latest_price_usd)}
                </p>
              </Td>
              <Td>
                <p className="text-right text-th-fgd-1">
                  ${numberCompacter.format(volume_24h_usd)}
                </p>
              </Td>
            </TrBody>
          )
        })}
      </tbody>
    </Table>
  )
}

export default SearchTable
